import React, {useContext} from 'react';

import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {Box, Chip, Divider, Grid, SxProps, Typography} from '@mui/material';

import {EmpionButton} from '../EmpionButton/EmpionButton';

import FallbackLight from '../../assets/job_postings/Fallback-light.png';
import {LanguageContext} from '../../contexts/LanguageContext/LanguageContext';
import {UserContext} from '../../contexts/UserContext/UserContext';
import {Match} from '../../features/applicant/dashboard/types/ApplicantMatchesTypes';
import {useFallBackLogo} from '../../hooks/useFallBackLogo';
import {greenColors} from '../../layouts/theme/designSystem';
import {AppConfig} from '../../lib/AppConfig';
import {ApplicantRoleData} from '../../types/User';
import {calculateDuration} from '../../utils/dateUtils/dateFormattingUtils';
import {formatEuro} from '../../utils/numberFormattingUtils/numberFormattingUtils';

interface CardMatchViewProps {
  match: Match;
  statusColour: string;
}

export default function CardMatchView({match, statusColour}: CardMatchViewProps) {
  const {t} = useTranslation();
  const logoUrl = useFallBackLogo(match.company.logoUrl);
  const {currentUser} = useContext(UserContext);
  const navigate = useNavigate();
  const currentLanguage = useContext(LanguageContext);

  const typographyHeaderStyles: SxProps = {
    color: 'text.secondary',
  };

  const typographyBodyStyles: SxProps = {
    color: 'text.secondary',
  };

  const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const onCultureFitClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const surveyId = (currentUser?.role.data as ApplicantRoleData).talentSurveyId;

    if (surveyId) {
      navigate(`${AppConfig.TALENT_SURVEY_WITH_ID_PATH}/${surveyId}`);
    }
  };

  return (
    <Box p={2} minWidth={'362px'}>
      <Grid container justifyContent={'space-between'}>
        <Grid container alignItems='center'>
          <Grid item display='flex' flexDirection='column' xs={8}>
            <Box
              component={'img'}
              src={logoUrl}
              alt={match.company.name}
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                const img = e.currentTarget;
                if (img.src !== FallbackLight) {
                  img.onerror = null;
                  img.src = FallbackLight;
                }
              }}
              sx={{width: '44px', height: '44px', objectFit: 'contain', borderRadius: '6px'}}
            />
          </Grid>
        </Grid>

        <Grid item mt={1}>
          <Typography variant='h4' sx={typographyHeaderStyles} gutterBottom>
            {match.company.name}
          </Typography>
          <Typography variant='body1' sx={typographyHeaderStyles}>
            {match.companyJobPosting.title}
          </Typography>
        </Grid>
      </Grid>

      <Divider sx={{my: 2, backgroundColor: 'white', height: '1px'}} />

      <Grid container>
        <Grid container item xs={4} flexDirection={'column'}>
          <Grid item>
            <Typography sx={typographyBodyStyles} variant='body1'>
              {t('companyJobPostings.jobPostingsFilter.location')}:
            </Typography>
          </Grid>
          <Grid item>
            <Typography sx={typographyBodyStyles} variant='body1'>
              {t('companyApplicants.applicantsCard.cardDetails.salary')}:
            </Typography>
          </Grid>
          <Grid item>
            <Typography sx={typographyBodyStyles} variant='body1'>
              {t('applicantDashboard.matchesView.cultureFit')}:
            </Typography>
          </Grid>
        </Grid>
        <Grid container item xs={8} flexDirection={'column'}>
          <Grid item>
            <Typography sx={typographyBodyStyles} variant='body1'>
              {match?.companyJobPosting.locations[0]?.city ?? '-'}
            </Typography>
          </Grid>
          <Grid item>
            <Typography sx={typographyBodyStyles} variant='body1'>
              {`${formatEuro(match.companyJobPosting.salary?.min)} - ${formatEuro(
                match.companyJobPosting.salary?.max
              )}`}
            </Typography>
          </Grid>
          <Grid item>
            {match.culturalFitScore ? (
              <Typography sx={typographyBodyStyles} variant='body1'>
                {match.culturalFitScore}
              </Typography>
            ) : (
              <EmpionButton sx={{padding: '0 16px'}} onClick={onCultureFitClick}>
                <Typography sx={{fontSize: '16px', fontWeight: 700}} variant='body1'>
                  {t('applicantCultureTypeBanner.applicant.button')}
                </Typography>
              </EmpionButton>
            )}
          </Grid>
        </Grid>
      </Grid>

      <Divider sx={{my: 2, backgroundColor: 'white', height: '1px'}} />

      <Grid container>
        <Grid container item xs={4}>
          <Typography sx={typographyBodyStyles} variant='body1' gutterBottom>
            {t('applicantDashboard.matchesView.lastAction')}:
          </Typography>
        </Grid>
        <Grid container item xs={8} justifyContent={'start'} alignContent={'center'}>
          <Typography sx={typographyBodyStyles} variant='body1' gutterBottom>
            {t('applicantDashboard.matchesView.daysAgo', {
              days: calculateDuration(match.updatedAt, new Date(), currentLanguage),
            })}
          </Typography>
        </Grid>
        <Chip
          label={capitalizeFirstLetter(match.status)}
          sx={{
            backgroundColor: statusColour,
            color: statusColour === greenColors[100] ? 'white' : greenColors[2],
            borderRadius: '4px',
            height: '30px',
            fontSize: '20px',
            p: '0px 8px',
          }}
        />
      </Grid>
    </Box>
  );
}
